// UserContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { BudgetGroup, UserProfile } from '../types/budgethelper';
import { auth, firebaseDB } from '../firebase';

interface UserContextType {
  userProfile: UserProfile | null;
  budgetGroups: BudgetGroup[];
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [budgetGroups, setBudgetGroups] = useState<BudgetGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setUserProfile(null);
        setLoading(false);
        return;
      }

      const userDocRef = doc(firebaseDB, "users", user.uid);
      const budgetGroupUnsubscribers: (() => void)[] = [];

      const unsubscribeUserSnap = onSnapshot(userDocRef, (snap) => {
        const data = snap.data();
        const budgetGroupIds = data?.budgetGroups || [];

        // Clean up old budget group listeners
        budgetGroupUnsubscribers.forEach(unsub => unsub());
        budgetGroupUnsubscribers.length = 0;

        if (budgetGroupIds.length === 0) {
          setUserProfile({
            userId: user.uid,
            friendlyName: data?.friendlyName || user.displayName,
            budgetGroups: [],
            defaultBudgetGroup: data?.defaultBudgetGroup,
            joinedAt: data?.joinedAt
          });
          setBudgetGroups([]);
          setLoading(false);
          return;
        }

        // Set up listeners for each budget group
        const budgetGroupsMap = new Map<string, BudgetGroup>();
        let loadedCount = 0;

        budgetGroupIds.forEach((groupId: string) => {
          const groupDocRef = doc(firebaseDB, "budgetGroups", groupId);
          const unsubscribeGroup = onSnapshot(groupDocRef, (groupSnap) => {
            if (groupSnap.exists()) {
              const groupData = groupSnap.data();
              budgetGroupsMap.set(groupId, {
                id: groupId,
                friendlyName: groupData.friendlyName,
                userIds: groupData.userIds,
                createdAt: groupData.createdAt,
              } as BudgetGroup);
            } else {
              budgetGroupsMap.delete(groupId);
            }

            loadedCount++;
            
            // Update user profile with current budget groups
            if (loadedCount >= budgetGroupIds.length || budgetGroupsMap.size > 0) {
              setUserProfile({
                userId: user.uid,
                friendlyName: data?.friendlyName || user.displayName,
                budgetGroups: budgetGroupIds,
                defaultBudgetGroup: data?.defaultBudgetGroup,
                joinedAt: data?.joinedAt
              });
              setBudgetGroups(Array.from(budgetGroupsMap.values()));
              setLoading(false);
            }
          }, (error) => {
            console.error(`Error listening to budget group ${groupId}:`, error);
          });

          budgetGroupUnsubscribers.push(unsubscribeGroup);
        });
      }, (error) => {
        console.error("Firestore error:", error);
        setLoading(false);
      });

      return () => {
        unsubscribeUserSnap();
        budgetGroupUnsubscribers.forEach(unsub => unsub());
      };
    });

    return () => unsubscribeAuth();
  }, []);

  return (
    <UserContext.Provider value={{ userProfile, budgetGroups, loading }}>
      {children}
    </UserContext.Provider>
  );
};

// 2. Create a custom hook for easy access
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};